import { useEffect, useRef, useState } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Bot, Send, X, Loader2, Sparkles, MessageSquarePlus, Mic, MicOff, Zap, ZapOff, Volume2, VolumeX, Bot as BotIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { useSpeechSynthesis } from "@/hooks/useSpeechSynthesis";

const STORAGE_KEY = "vywork_coworker_session";
const AUTOSEND_KEY = "vywork_coworker_autosend";
const TTS_KEY = "vywork_coworker_tts";
const AUTONOMOUS_KEY = "vywork_coworker_autonomous";

/**
 * Bereinigt Assistant-Text für die Sprachausgabe:
 * - entfernt Markdown-Zeichen (*, _, `, #, >)
 * - entfernt gängige Status-Emojis (✅ ⚠️ ❌ ℹ️ 🟡 🟢 🔴)
 * - kürzt „(ID abc12345…)" heraus (nicht sinnvoll zu hören)
 * - kürzt lange UUID-artige Hex-Sequenzen
 * - kollabiert Whitespace
 */
function cleanForTts(input) {
  return String(input || "")
    .replace(/\(ID [^)]*\)/gi, "")
    .replace(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, "")
    .replace(/[a-f0-9]{16,}/gi, "")
    .replace(/[*_`#>]/g, "")
    .replace(/[✅⚠️❌ℹ️🟡🟢🔴📌📎🔧👤]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const CoWorkerWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // {token, tool, args}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [autoSend, setAutoSend] = useState(() => localStorage.getItem(AUTOSEND_KEY) === "1");
  const [ttsEnabled, setTtsEnabled] = useState(() => localStorage.getItem(TTS_KEY) === "1");
  const [autonomous, setAutonomous] = useState(() => localStorage.getItem(AUTONOMOUS_KEY) === "1");
  const scrollRef = useRef(null);
  const inputRef = useRef("");
  const shouldAutoSendRef = useRef(false);
  const inputFocusedRef = useRef(false);
  const pttActiveRef = useRef(false);
  const lastSpokenIdxRef = useRef(-1);

  // keep ref in sync so voice callbacks always see fresh value
  useEffect(() => { inputRef.current = input; }, [input]);

  const tts = useSpeechSynthesis({ language: "de-DE" });

  const voice = useVoiceInput({
    language: "de-DE",
    onTranscript: (text, isFinal) => {
      if (!text) return;
      setInput(text);
      if (isFinal && shouldAutoSendRef.current) {
        shouldAutoSendRef.current = false;
        // small delay so UI updates the input first
        setTimeout(() => sendMessageWithText(text), 50);
      }
    },
  });

  useEffect(() => {
    if (open && sessionId) loadHistory(sessionId);
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  const loadHistory = async (sid) => {
    try {
      const res = await api.get(`/api/coworker/history/${sid}`);
      setMessages(res.data?.messages || []);
    } catch { /* ignore */ }
  };

  const startNewSession = () => {
    setSessionId("");
    localStorage.removeItem(STORAGE_KEY);
    setMessages([]);
    setPending(null);
  };

  const sendMessageWithText = async (text) => {
    text = (text || "").trim();
    if (!text || busy) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const res = await api.post("/api/coworker/chat", { message: text, session_id: sessionId || null, autonomous });
      const sid = res.data.session_id;
      if (sid && sid !== sessionId) {
        setSessionId(sid);
        localStorage.setItem(STORAGE_KEY, sid);
      }
      setMessages(m => [...m, { role: "assistant", content: res.data.reply || "" }]);
      if (res.data.pending_confirmation) {
        setPending(res.data.pending_confirmation);
        setConfirmOpen(true);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Fehler beim Senden";
      setMessages(m => [...m, { role: "assistant", content: `⚠️ ${msg}` }]);
    } finally {
      setBusy(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    await sendMessageWithText(inputRef.current);
  };

  const toggleAutoSend = () => {
    setAutoSend(prev => {
      const next = !prev;
      localStorage.setItem(AUTOSEND_KEY, next ? "1" : "0");
      toast.info(next ? "Automatisches Senden aktiviert" : "Automatisches Senden deaktiviert");
      return next;
    });
  };

  const toggleTts = () => {
    setTtsEnabled(prev => {
      const next = !prev;
      localStorage.setItem(TTS_KEY, next ? "1" : "0");
      if (!next) tts.stop();
      if (next && !tts.supported) {
        toast.error("Sprachausgabe wird von diesem Browser nicht unterstützt.");
        return false;
      }
      toast.info(next ? "Sprachausgabe aktiviert" : "Sprachausgabe deaktiviert");
      return next;
    });
  };

  const toggleAutonomous = () => {
    setAutonomous(prev => {
      const next = !prev;
      localStorage.setItem(AUTONOMOUS_KEY, next ? "1" : "0");
      toast[next ? "warning" : "info"](
        next
          ? "Autonom-Modus AN — Aktionen werden ohne Rückfrage ausgeführt (außer Fahrzeug anlegen)"
          : "Autonom-Modus AUS — jede Aktion wird einzeln bestätigt"
      );
      return next;
    });
  };

  const speakMessage = (text) => {
    if (!tts.supported) {
      toast.error("Sprachausgabe wird von diesem Browser nicht unterstützt.");
      return;
    }
    if (tts.speaking) tts.stop(); else tts.speak(cleanForTts(text));
  };

  // Auto-speak: sprich neueste Assistant-Nachricht, wenn TTS an ist
  useEffect(() => {
    if (!ttsEnabled || !tts.supported || messages.length === 0) return;
    const lastIdx = messages.length - 1;
    if (lastIdx <= lastSpokenIdxRef.current) return;
    const last = messages[lastIdx];
    if (last.role === "assistant" && last.content) {
      const clean = cleanForTts(last.content);
      if (clean) tts.speak(clean);
      lastSpokenIdxRef.current = lastIdx;
    } else {
      lastSpokenIdxRef.current = lastIdx;
    }
  }, [messages, ttsEnabled, tts]);

  const handleMicClick = () => {
    if (voice.recording) {
      voice.stop();
      return;
    }
    if (!voice.supported) {
      toast.error("Spracheingabe wird von diesem Browser nicht unterstützt.");
      return;
    }
    shouldAutoSendRef.current = autoSend;
    voice.start();
  };

  useEffect(() => {
    if (!voice.error) return;
    const map = {
      "not-allowed": "Mikrofon-Zugriff wurde verweigert.",
      "mic-permission-denied": "Mikrofon-Zugriff wurde verweigert.",
      "no-speech": "Nichts gehört – bitte lauter oder näher zum Mikrofon.",
      "not-supported": "Spracheingabe wird von diesem Browser nicht unterstützt.",
      "empty-recording": "Aufnahme war leer.",
      "empty-transcript": "Keine Sprache erkannt.",
      "audio-capture": "Kein Mikrofon gefunden.",
      "network": "Netzwerkfehler bei Spracherkennung.",
    };
    toast.error(map[voice.error] || `Sprachfehler: ${voice.error}`);
  }, [voice.error]);

  // Push-to-Talk: Leertaste halten startet Aufnahme (nur wenn Widget offen
  // und Textfeld NICHT fokussiert). Loslassen stoppt + sendet automatisch.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.code !== "Space" || e.repeat) return;
      // Wenn im Textfeld getippt wird → normale Leertaste
      if (inputFocusedRef.current) return;
      // Wenn in irgendeinem anderen input/textarea/contenteditable
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (busy || voice.transcribing) return;
      e.preventDefault();
      if (voice.recording) return;
      if (!voice.supported) return;
      pttActiveRef.current = true;
      shouldAutoSendRef.current = true; // PTT sendet immer automatisch
      voice.start();
    };
    const onKeyUp = (e) => {
      if (e.code !== "Space") return;
      if (!pttActiveRef.current) return;
      pttActiveRef.current = false;
      e.preventDefault();
      if (voice.recording) voice.stop();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [open, busy, voice]);


  const respondConfirm = async (approved) => {
    if (!pending || !sessionId) { setConfirmOpen(false); setPending(null); return; }
    setBusy(true);
    try {
      const res = await api.post("/api/coworker/confirm", { session_id: sessionId, approved });
      setMessages(m => [...m, { role: "assistant", content: res.data.reply || "" }]);
      if (res.data.executed) toast.success("Aktion ausgeführt");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bestätigung fehlgeschlagen");
    } finally {
      setBusy(false);
      setConfirmOpen(false);
      setPending(null);
    }
  };

  const suggestions = [
    "Wie viele offene Aufträge?",
    "Suche Kunde nach Namen",
    "Lagerbestand von Bremsscheibe prüfen",
    "Status der offenen Produktionen",
  ];

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 text-white shadow-2xl flex items-center justify-center hover:scale-110 transition-transform group"
          data-testid="coworker-open-btn"
          aria-label="KI-Co-Worker öffnen"
        >
          <Bot className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 animate-pulse" />
          <span className="pointer-events-none absolute right-16 whitespace-nowrap bg-slate-900 text-white text-xs rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
            KI-Co-Worker
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-3rem)] h-[560px] max-h-[calc(100vh-3rem)] rounded-2xl border shadow-2xl bg-background flex flex-col overflow-hidden" data-testid="coworker-panel">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-gradient-to-r from-slate-900 to-slate-700 text-white">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="font-semibold text-sm">KI-Co-Worker</div>
                <div className="text-xs opacity-75">Claude Sonnet · Live-Daten</div>
              </div>
            </div>
            <div className="flex gap-1">
              <button
                onClick={toggleAutonomous}
                title={autonomous ? "Autonom-Modus AN — Aktionen ohne Rückfrage (außer Fahrzeug)" : "Autonom-Modus AUS — jede Aktion einzeln bestätigen"}
                className={`p-1.5 rounded transition-colors ${autonomous ? "bg-amber-500/30 text-amber-200" : "hover:bg-white/10"}`}
                data-testid="coworker-autonomous-toggle"
              >
                {autonomous ? <BotIcon className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
              </button>
              <button
                onClick={toggleTts}
                title={ttsEnabled ? "Sprachausgabe: an (klicken zum Deaktivieren)" : "Sprachausgabe: aus (klicken zum Aktivieren)"}
                className={`p-1.5 rounded transition-colors ${ttsEnabled ? "bg-white/20 text-emerald-300" : "hover:bg-white/10"}`}
                data-testid="coworker-tts-toggle"
              >
                {ttsEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button onClick={startNewSession} title="Neue Session" className="p-1.5 hover:bg-white/10 rounded" data-testid="coworker-new-session">
                <MessageSquarePlus className="w-4 h-4" />
              </button>
              <button onClick={() => setOpen(false)} title="Schließen" className="p-1.5 hover:bg-white/10 rounded" data-testid="coworker-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50/50" data-testid="coworker-messages">
            {messages.length === 0 && (
              <div className="text-center py-6 space-y-3">
                <Bot className="w-10 h-10 mx-auto text-slate-400" />
                <p className="text-sm text-muted-foreground">Frag mich alles über deine Kunden, Aufträge, Bestand oder Produktion.</p>
                <div className="grid gap-1.5 mt-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(s)}
                      className="text-xs text-left px-2.5 py-1.5 rounded border bg-white hover:border-primary hover:text-primary transition-colors"
                      data-testid={`coworker-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[85%] group">
                  <div className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user" ? "bg-primary text-primary-foreground" : "bg-white border shadow-sm"
                  }`}>
                    {m.content}
                  </div>
                  {m.role === "assistant" && tts.supported && m.content && (
                    <button
                      type="button"
                      onClick={() => speakMessage(m.content)}
                      className="mt-1 text-[10px] flex items-center gap-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                      title={tts.speaking ? "Wiedergabe stoppen" : "Vorlesen"}
                      data-testid={`coworker-speak-msg-${i}`}
                    >
                      {tts.speaking ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                      {tts.speaking ? "Stopp" : "Vorlesen"}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> denkt nach…
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-3 border-t bg-background space-y-2">
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <button
                type="button"
                onClick={toggleAutoSend}
                className="flex items-center gap-1 hover:text-foreground transition-colors"
                title={autoSend ? "Sprachnachricht wird nach dem Sprechen automatisch gesendet" : "Sprachnachricht landet nur im Eingabefeld"}
                data-testid="coworker-autosend-toggle"
              >
                {autoSend ? <Zap className="w-3 h-3 text-emerald-500" /> : <ZapOff className="w-3 h-3" />}
                <span>Auto-Senden: <strong>{autoSend ? "an" : "aus"}</strong></span>
              </button>
              {voice.recording && (
                <span className="flex items-center gap-1 text-red-500 font-medium" data-testid="coworker-voice-recording-indicator">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  {pttActiveRef.current ? "PTT läuft…" : "Aufnahme läuft…"}
                </span>
              )}
              {voice.transcribing && (
                <span className="flex items-center gap-1 text-slate-500" data-testid="coworker-voice-transcribing-indicator">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Transkribiere…
                </span>
              )}
              {!voice.recording && !voice.transcribing && voice.supported && (
                <span className="text-[10px] opacity-60" data-testid="coworker-ptt-hint">
                  <kbd className="px-1 rounded bg-slate-200 border text-[9px]">Leertaste</kbd> halten zum Sprechen
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={e => setInput(e.target.value)}
                onFocus={() => { inputFocusedRef.current = true; }}
                onBlur={() => { inputFocusedRef.current = false; }}
                placeholder={voice.recording ? "Sprich jetzt…" : "Frage stellen oder Aufgabe geben…"}
                disabled={busy}
                data-testid="coworker-input"
              />
              <Button
                type="button"
                onClick={handleMicClick}
                disabled={busy || voice.transcribing || !voice.supported}
                size="icon"
                variant={voice.recording ? "destructive" : "outline"}
                title={
                  !voice.supported ? "Nicht unterstützt"
                    : voice.recording ? "Aufnahme stoppen"
                    : "Spracheingabe starten"
                }
                data-testid="coworker-voice-btn"
                className={voice.recording ? "animate-pulse" : ""}
              >
                {voice.recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              <Button type="submit" disabled={busy || !input.trim()} size="icon" data-testid="coworker-send-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Confirmation dialog for write actions */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="coworker-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Aktion bestätigen?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>Die KI möchte folgende Aktion ausführen:</div>
                <div className="p-2 rounded bg-slate-50 border font-mono text-xs">
                  <div><strong>Tool:</strong> {pending?.tool}</div>
                  <div><strong>Parameter:</strong> {JSON.stringify(pending?.args, null, 2)}</div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => respondConfirm(false)}>Ablehnen</AlertDialogCancel>
            <AlertDialogAction onClick={() => respondConfirm(true)} data-testid="coworker-confirm-ok">
              Ausführen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CoWorkerWidget;
